import {
  createMemberUpdateAuthorization,
  getMemberUpdateAuthorizations,
  getMemberUpdateCapabilities,
  revokeMemberUpdateAuthorization,
  type MemberUpdateAuthorizationContract,
  type MemberUpdateCapabilityContract,
} from '@urbanik/api-client';
import { CanonicalModal, formatDateTime } from '@urbanik/ui';
import { Ban, Clock3, KeyRound, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
type Draft = {
  startsAt: string;
  expiresAt: string;
  maxSubmissions: number;
  fieldCodes: string[];
  reason: string;
};
const localDateTime = (date: Date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};
const emptyDraft = (): Draft => {
  const now = new Date(),
    end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return {
    startsAt: localDateTime(now),
    expiresAt: localDateTime(end),
    maxSubmissions: 1,
    fieldCodes: [],
    reason: '',
  };
};
const labels: Record<MemberUpdateAuthorizationContract['status'], string> = {
  scheduled: 'Programada',
  active: 'Activa',
  expired: 'Vencida',
  revoked: 'Revocada',
  consumed: 'Consumida',
};
export function MemberUpdateAuthorizationCard({
  memberId,
  canManage,
  embedded = false,
}: {
  memberId: string;
  canManage: boolean;
  embedded?: boolean;
}) {
  const [linkedUserId, setLinkedUserId] = useState<string | null>(null);
  const [items, setItems] = useState<MemberUpdateAuthorizationContract[]>([]);
  const [capabilities, setCapabilities] = useState<MemberUpdateCapabilityContract[]>([]);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<MemberUpdateAuthorizationContract | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = () => {
    setError('');
    void Promise.all([
      getMemberUpdateAuthorizations(apiBase, memberId),
      getMemberUpdateCapabilities(apiBase),
    ])
      .then(([result, catalog]) => {
        setLinkedUserId(result.linkedUserId);
        setItems(result.authorizations);
        setCapabilities(catalog.capabilities);
      })
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : 'No pudimos cargar las autorizaciones.'),
      );
  };
  useEffect(load, [memberId]);
  const effective =
    items.find((item) => item.status === 'active' || item.status === 'scheduled') ?? null;
  const sections = useMemo(
    () => [...new Set(capabilities.map((item) => item.sectionCode))],
    [capabilities],
  );
  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await createMemberUpdateAuthorization(apiBase, memberId, {
        startsAt: new Date(draft.startsAt).toISOString(),
        expiresAt: new Date(draft.expiresAt).toISOString(),
        maxSubmissions: draft.maxSubmissions,
        fieldCodes: draft.fieldCodes,
        reason: draft.reason,
      });
      setLinkedUserId(result.linkedUserId);
      setItems(result.authorizations);
      setCreateOpen(false);
      setDraft(emptyDraft());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No pudimos crear la autorización.');
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    if (!revokeTarget) return;
    setBusy(true);
    setError('');
    try {
      const result = await revokeMemberUpdateAuthorization(apiBase, memberId, revokeTarget.id, {
        expectedVersion: revokeTarget.version,
        reason: revokeReason,
      });
      setItems(result.authorizations);
      setRevokeTarget(null);
      setRevokeReason('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No pudimos revocar la autorización.');
    } finally {
      setBusy(false);
    }
  };
  const toggle = (fieldCode: string) =>
    setDraft({
      ...draft,
      fieldCodes: draft.fieldCodes.includes(fieldCode)
        ? draft.fieldCodes.filter((code) => code !== fieldCode)
        : [...draft.fieldCodes, fieldCode],
    });
  return (
    <section
      className={`member-profile-expanded member-update-authorization-card${embedded ? ' member-admin-component--embedded' : ''}`}
    >
      <div className="member-profile-section-heading">
        <div>
          <p className="kicker">Acceso temporal</p>
          <h2>Actualización autorizada</h2>
        </div>
        {canManage && linkedUserId && !effective ? (
          <button
            className="ui-button ui-button--primary"
            onClick={() => {
              setError('');
              setCreateOpen(true);
            }}
            aria-label="Conceder autorización"
          >
            <KeyRound aria-hidden="true" /> Conceder
          </button>
        ) : null}
      </div>
      {error && !createOpen && !revokeTarget ? (
        <p className="member-status-error" role="alert">
          {error}
        </p>
      ) : null}
      {!linkedUserId ? (
        <div className="member-authorization-blocked">
          <Ban aria-hidden="true" />
          <div>
            <strong>Sin usuario vinculado</strong>
            <p>Primero debe existir un vínculo activo entre la identidad de acceso y este socio.</p>
          </div>
        </div>
      ) : effective ? (
        <article className="member-authorization-current">
          <div>
            <span
              className={`ui-status ui-status--${effective.status === 'active' ? 'ready' : 'review'}`}
            >
              <ShieldCheck aria-hidden="true" /> {labels[effective.status]}
            </span>
            <strong>{effective.scopes.length} campos autorizados</strong>
          </div>
          <dl>
            <div>
              <dt>Inicio</dt>
              <dd>{formatDateTime(effective.startsAt)}</dd>
            </div>
            <div>
              <dt>Vencimiento</dt>
              <dd>{formatDateTime(effective.expiresAt)}</dd>
            </div>
            <div>
              <dt>Envíos</dt>
              <dd>
                {effective.usedSubmissions}/{effective.maxSubmissions}
              </dd>
            </div>
          </dl>
          <p>{effective.scopes.map((scope) => scope.label).join(' · ')}</p>
          {canManage ? (
            <button
              className="ui-button"
              onClick={() => {
                setError('');
                setRevokeTarget(effective);
              }}
            >
              <Ban aria-hidden="true" /> Revocar
            </button>
          ) : null}
        </article>
      ) : (
        <div className="member-household-empty">
          <Clock3 aria-hidden="true" />
          <p>No existe una autorización vigente.</p>
        </div>
      )}
      {items.length ? (
        <details className="member-location-history">
          <summary>
            <Clock3 aria-hidden="true" /> Historial ({items.length})
          </summary>
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                <strong>
                  {labels[item.status]} · {item.scopes.length} campos
                </strong>
                <span>
                  {formatDateTime(item.startsAt)} → {formatDateTime(item.expiresAt)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      <CanonicalModal
        open={createOpen}
        size="large"
        eyebrow="Acceso temporal"
        title="Conceder autorización"
        description="Define exactamente qué datos podrá proponer el socio y durante cuánto tiempo."
        onClose={() => !busy && setCreateOpen(false)}
      >
        <form
          className="member-authorization-form"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <div className="member-location-grid">
            <label>
              Inicio *
              <input
                className="ui-input"
                type="datetime-local"
                required
                value={draft.startsAt}
                onChange={(event) => setDraft({ ...draft, startsAt: event.target.value })}
              />
            </label>
            <label>
              Vencimiento *
              <input
                className="ui-input"
                type="datetime-local"
                required
                value={draft.expiresAt}
                onChange={(event) => setDraft({ ...draft, expiresAt: event.target.value })}
              />
            </label>
            <label>
              Máximo de envíos *
              <input
                className="ui-input"
                type="number"
                min={1}
                max={5}
                required
                value={draft.maxSubmissions}
                onChange={(event) =>
                  setDraft({ ...draft, maxSubmissions: Number(event.target.value) })
                }
              />
            </label>
          </div>
          <fieldset className="member-authorization-scopes">
            <legend>Campos autorizados *</legend>
            {sections.map((section) => (
              <section key={section}>
                <h3>{section}</h3>
                {capabilities
                  .filter((item) => item.sectionCode === section)
                  .map((item) => (
                    <label key={item.fieldCode}>
                      <input
                        type="checkbox"
                        checked={draft.fieldCodes.includes(item.fieldCode)}
                        onChange={() => toggle(item.fieldCode)}
                      />
                      <span>
                        {item.label}
                        <small>
                          {item.sensitivity === 'sensitive'
                            ? 'Información sensible'
                            : 'Información personal'}
                        </small>
                      </span>
                    </label>
                  ))}
              </section>
            ))}
          </fieldset>
          <label className="ui-field">
            <span>Motivo administrativo *</span>
            <textarea
              required
              minLength={10}
              maxLength={500}
              value={draft.reason}
              onChange={(event) => setDraft({ ...draft, reason: event.target.value })}
            />
          </label>
          {error ? (
            <p className="member-status-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="member-status-actions">
            <button
              type="button"
              className="ui-button"
              onClick={() => setCreateOpen(false)}
              disabled={busy}
            >
              Cancelar
            </button>
            <button
              className="ui-button ui-button--primary"
              disabled={busy || !draft.fieldCodes.length}
            >
              {busy ? 'Guardando…' : 'Conceder autorización'}
            </button>
          </div>
        </form>
      </CanonicalModal>
      <CanonicalModal
        open={Boolean(revokeTarget)}
        size="medium"
        eyebrow="Acceso temporal"
        title="Revocar autorización"
        description="La revocación será inmediata y quedará auditada."
        onClose={() => !busy && setRevokeTarget(null)}
      >
        <form
          className="member-authorization-form"
          onSubmit={(event) => {
            event.preventDefault();
            void revoke();
          }}
        >
          <label className="ui-field">
            <span>Motivo de revocación *</span>
            <textarea
              required
              minLength={10}
              maxLength={500}
              value={revokeReason}
              onChange={(event) => setRevokeReason(event.target.value)}
            />
          </label>
          {error ? (
            <p className="member-status-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="member-status-actions">
            <button
              type="button"
              className="ui-button"
              onClick={() => setRevokeTarget(null)}
              disabled={busy}
            >
              Cancelar
            </button>
            <button
              className="ui-button ui-button--primary"
              disabled={busy || revokeReason.trim().length < 10}
            >
              {busy ? 'Revocando…' : 'Confirmar revocación'}
            </button>
          </div>
        </form>
      </CanonicalModal>
    </section>
  );
}
